import * as React from "react";
import { useSearchParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import { useAdminWebhook, useAdminWebhooks } from "@/hooks/useAdmin";
import { cn, formatDate } from "@/lib/utils";

const STATUS_OPTIONS = ["processed", "ignored", "failed"];

export function AdminWebhooksPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";

  const { data, isLoading } = useAdminWebhooks({ status, page: 1, pageSize: 200 });
  const [openId, setOpenId] = React.useState<number | null>(null);

  function setStatus(s: string) {
    const next = new URLSearchParams(params);
    if (s) next.set("status", s); else next.delete("status");
    setParams(next, { replace: true });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">Webhooks</h2>
          <p className="text-sm text-muted-foreground">Inbound event log — click a row for raw payload.</p>
        </div>
        <div className="flex flex-wrap gap-1">
          <Filter label="All" active={status === ""} onClick={() => setStatus("")} />
          {STATUS_OPTIONS.map(s => (
            <Filter key={s} label={cap(s)} active={status === s} onClick={() => setStatus(s)} />
          ))}
        </div>
      </div>

      {isLoading ? (
        <Card><CardContent className="space-y-2 py-5">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </CardContent></Card>
      ) : !data || data.items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          No webhook events recorded.
        </CardContent></Card>
      ) : (
        <>
          <Card className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead>Received</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map(w => (
                  <TableRow key={w.id} className="cursor-pointer" onClick={() => setOpenId(w.id)}>
                    <TableCell className="font-mono text-xs">{w.event_type}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{w.source}</TableCell>
                    <TableCell><StatusBadge status={w.status} /></TableCell>
                    <TableCell className="max-w-xs truncate text-sm text-muted-foreground">{w.note ?? "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDate(w.received_at)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm">View</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <div className="grid gap-3 md:hidden">
            {data.items.map(w => (
              <Card key={w.id} onClick={() => setOpenId(w.id)} className="cursor-pointer">
                <CardContent className="space-y-1 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="truncate font-mono text-xs">{w.event_type}</p>
                    <StatusBadge status={w.status} />
                  </div>
                  <p className="truncate text-xs text-muted-foreground">{w.note ?? "—"}</p>
                  <p className="text-[11px] text-muted-foreground">{w.source} · {formatDate(w.received_at)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      <WebhookDetailDialog id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function WebhookDetailDialog({ id, onClose }: { id: number | null; onClose: () => void }) {
  const { data, isLoading } = useAdminWebhook(id ?? undefined);
  return (
    <Dialog open={id !== null} onOpenChange={next => { if (!next) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Webhook event {id ? `#${id}` : ""}</DialogTitle>
          <DialogDescription>Raw payload as received from the upstream provider.</DialogDescription>
        </DialogHeader>
        {isLoading || !data ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-3">
              <Field label="Source"      value={data.source} />
              <Field label="Event type"  value={data.event_type} />
              <Field label="Event ID"    value={data.event_id} mono />
              <Field label="Status"      value={data.status} />
              <Field label="Received"    value={formatDate(data.received_at)} />
              <Field label="Processed"   value={formatDate(data.processed_at)} />
            </div>
            {data.note && (
              <div className="rounded-md border border-border bg-muted/50 p-3 text-sm">{data.note}</div>
            )}
            {data.raw_payload && (
              <pre className="max-h-[40vh] overflow-auto rounded-md border border-border bg-muted/50 p-3 text-xs">
                {tryPretty(data.raw_payload)}
              </pre>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function tryPretty(json: string): string {
  try { return JSON.stringify(JSON.parse(json), null, 2); }
  catch { return json; }
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={cn("mt-0.5", mono ? "font-mono" : "font-medium")}>{value}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant: React.ComponentProps<typeof Badge>["variant"] =
    status === "processed" ? "success"
    : status === "failed"  ? "destructive"
    :                        "outline";
  return <Badge variant={variant} className="uppercase">{status}</Badge>;
}

function Filter({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        active ? "border-brand-accent bg-brand-accentSoft text-brand-accentDark"
               : "border-border text-muted-foreground hover:bg-muted"
      )}
    >{label}</button>
  );
}

function cap(s: string) { return s.charAt(0).toUpperCase() + s.slice(1); }
